import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import {
  IWebsiteBuilderInputSettings,
  TWebsiteBuilderInputType,
} from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderInput";
import { Select } from "components/select/index";
import Input from "components/form/input/Input";
import Checkbox from "components/form/checkbox/Checkbox";
import { useAppDispatch } from "hooks/redux";
import { websiteBuilderUpdateElementSettings } from "redux/actions/websiteBuilder/websiteBuilder";
import {
  IWebsiteBuilderPopupOption,
  renderWebsiteBuilderPopupOptions,
} from "../../../../helpers/popup/renderWebsiteBuilderPopupOptions";
import { WebsiteBuilderPopupSections } from "../../../../helpers/popup/WebsiteBuilderPopupSections";
import {
  useWebsiteBuilderDebouncedUpdate,
  WebsiteBuilderDebouncedUpdateTime,
} from "pages/secure/facility/websiteBuilder/editor/hooks/debouncedUpdate/useWebsiteBuilderDebouncedUpdate";

interface IInputSettingsProps {
  id: string;
  settings: IWebsiteBuilderInputSettings;
}

export function InputSettings(props: IInputSettingsProps) {
  const { id, settings } = props;
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const [updatedSettings, setUpdatedSettings] = useState<IWebsiteBuilderInputSettings>(cloneDeep(settings));

  const typeOptions: IWebsiteBuilderPopupOption<TWebsiteBuilderInputType>[] = useMemo(() => {
    return [
      {
        label: t("Text"),
        value: "text",
      },
      {
        label: t("Email"),
        value: "email",
      },
      {
        label: t("Date"),
        value: "date",
      },
      {
        label: t("Number"),
        value: "number",
      },
      {
        label: t("Phone Number"),
        value: "phone-number",
      },
    ];
  }, []);

  function dispatchSettings(prevSettings: IWebsiteBuilderInputSettings, nextSettings: IWebsiteBuilderInputSettings) {
    dispatch(
      websiteBuilderUpdateElementSettings({
        id,
        prevElementSettings: {
          type: "input",
          settings: cloneDeep(prevSettings),
        },
        nextElementSettings: {
          type: "input",
          settings: cloneDeep(nextSettings),
        },
      }),
    );
  }

  const { handleUpdateState, handleDebouncedUpdateState } = useWebsiteBuilderDebouncedUpdate({
    state: settings,
    updatedState: updatedSettings,
    setUpdatedState: setUpdatedSettings,
    dispatchState: dispatchSettings,
    debouncedUpdateTime: WebsiteBuilderDebouncedUpdateTime.MEDIUM,
  });

  function renderTypeSection() {
    return (
      <Select
        label={t("Type")}
        defaultValue={updatedSettings.type}
        onChange={(type: TWebsiteBuilderInputType) =>
          handleUpdateState(prev => ({
            ...prev,
            type,
          }))
        }
      >
        {renderWebsiteBuilderPopupOptions(typeOptions)}
      </Select>
    );
  }

  function renderLabelSection() {
    return (
      <>
        <Input
          label={t("Label")}
          value={updatedSettings.label}
          onChange={e =>
            handleDebouncedUpdateState(prev => ({
              ...prev,
              label: e.target.value,
            }))
          }
        />
        <Input
          label={t("Placeholder")}
          value={updatedSettings.placeholder}
          onChange={e =>
            handleDebouncedUpdateState(prev => ({
              ...prev,
              placeholder: e.target.value,
            }))
          }
        />
      </>
    );
  }

  function renderRequiredSection() {
    return (
      <Checkbox
        label={t("Required")}
        size="medium"
        value={updatedSettings.required}
        checked={updatedSettings.required}
        onChange={e =>
          handleUpdateState(prev => ({
            ...prev,
            required: e.target.checked,
          }))
        }
      />
    );
  }

  return (
    <WebsiteBuilderPopupSections sections={[renderTypeSection(), renderLabelSection(), renderRequiredSection()]} />
  );
}
